import {Inject, Injectable, DOCUMENT} from '@angular/core';

import {AfterPageViewEvent} from './events/after-page-view-event';
import {CxEvent, EventService} from '@spartacus/core';
import {EddlStBeaconEvent} from './events/eddl-stbeacon-event';
import {EddlStlBeaconEvent} from './events/eddl-stlbeacon-event';
import {EddlSearchInitiatedEvent} from './events/eddl-search-initiated-event';
import {EddlFormSubmitEvent} from './events/eddl-form-submit-event';
import {EddlFormErrorEvent} from './events/eddl-form-error-event';
import {EddlDownloadAttemptEvent} from './events/eddl-download-attempt-event';
import {EddlDownloadSuccessEvent} from './events/eddl-download-success-event';
import {EddlFormViewEvent} from './events/eddl-form-view-event';
import {EddlRemoveFromCartEvent} from './events/eddl-remove-from-cart-event';
import {EddlSearchNoResultsEvent} from './events/eddl-search-no-results-event';
import {PageEvent} from '@spartacus/storefront';
import {EddlSearchResultsClickEvent} from './events/eddl-search-results-click-event';
import {EddlSearchFilterAppliedEvent} from './events/eddl-search-filter-applied-event';
import {EddlSearchFilterRemovedEvent} from './events/eddl-search-filter-removed-event';
import {EddlSearchFilterClearedEvent} from './events/eddl-search-filter-cleared-event';
import {EddlAddToCartEvent} from './events/eddl-add-to-cart-event';
import {EddlCartCreatedEvent} from './events/eddl-cart-created-event';
import {EddlCartViewEvent} from './events/eddl-cart-view-event';
import {EddlLogoutEvent} from '@app/services/analytics/EDDL/events/eddl-logout-event';
import {EddlLoginEvent} from '@app/services/analytics/EDDL/events/eddl-login-event';
import {ErrorPageEvent} from '@app/services/analytics/EDDL/events/error-page-event';
import {EddlBeaconKey} from '@app/services/analytics/EDDL/models/eddl-adobe-tracking-data';
import {EddlProductViewEvent} from '@app/services/analytics/EDDL/events/eddl-product-view-event';
import {EddlCheckoutStartEvent} from './events/eddl-checkout-start-event';

@Injectable({
  providedIn: 'root',
})
export class DcpEddlDispatchService {
  stBeaconEventList = [
    AfterPageViewEvent,
    PageEvent,
    EddlCartViewEvent,
    ErrorPageEvent,
    EddlProductViewEvent,
  ];
  stlBeaconEventList = [
    EddlCartCreatedEvent,
    EddlAddToCartEvent,
    EddlSearchInitiatedEvent,
    EddlSearchNoResultsEvent,
    EddlSearchResultsClickEvent,
    EddlSearchFilterAppliedEvent,
    EddlSearchFilterRemovedEvent,
    EddlSearchFilterClearedEvent,
    EddlFormSubmitEvent,
    EddlFormErrorEvent,
    EddlDownloadAttemptEvent,
    EddlDownloadSuccessEvent,
    EddlFormViewEvent,
    EddlRemoveFromCartEvent,
    EddlLogoutEvent,
    EddlLoginEvent,
  ];

  // Checkout page beacon coordination
  private checkoutBeaconPending = false;
  private checkoutEventsReceived = new Set<string>();

  constructor(@Inject(DOCUMENT) private document: Document, private events: EventService) {}

  /**
   * Reset checkout beacon state (call on navigation)
   */
  resetCheckoutBeacon(): void {
    this.checkoutBeaconPending = false;
    this.checkoutEventsReceived.clear();
  }

  /**
   * Start collecting checkout events for beacon
   */
  startCheckoutBeaconCollection(): void {
    this.checkoutBeaconPending = true;
    this.checkoutEventsReceived.clear();
  }

  /**
   * Record that a checkout event has been pushed
   */
  recordCheckoutEvent(eventType: 'checkoutStart' | 'pageView'): void {
    if (!this.checkoutBeaconPending) return;

    this.checkoutEventsReceived.add(eventType);

    // If both events received, trigger beacon
    if (this.checkoutEventsReceived.has('checkoutStart') && this.checkoutEventsReceived.has('pageView')) {
      this.checkoutBeaconPending = false;
      this.events.dispatch(new EddlStBeaconEvent());
    }
  }

  public dispatchAfterPageViewEvent(): void {
    this.events.dispatch(new AfterPageViewEvent());
  }

  public triggerBeaconEvents<T extends CxEvent>(event: T): void {
    // Handle checkout beacon collection mode
    if (this.checkoutBeaconPending) {
      // Record checkoutStart event
      if (event instanceof EddlCheckoutStartEvent) {
        this.recordCheckoutEvent('checkoutStart');
        return;
      }
      // Record pageView event on checkout pages
      const isCheckoutPage = this.document?.defaultView?.location?.pathname?.includes('/checkout/');
      if (event instanceof PageEvent && isCheckoutPage) {
        this.recordCheckoutEvent('pageView');
        return;
      }
    }

    const stBeaconMatches = this.stBeaconEventList.filter((className) => event instanceof className);
    const stlBeaconMatches = this.stlBeaconEventList.filter((className) => event instanceof className);

    if (stBeaconMatches.length > 0) {
      this.events.dispatch(new EddlStBeaconEvent());
    }
    if (stlBeaconMatches.length > 0) {
      this.events.dispatch(new EddlStlBeaconEvent());
    }
  }

  public triggerBeaconEventManually(type: EddlBeaconKey): void {
    this.events.dispatch(type === EddlBeaconKey.ST ? new EddlStBeaconEvent() : new EddlStlBeaconEvent());
  }
}
